import {
  BedIcon,
  CalendarDotsIcon,
  CopyIcon,
  MapPinIcon,
} from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { Divider } from "@/components/Divider/Divider";
import { IconButton } from "@/components/IconButton/IconButton";
import { Image } from "@/components/Image/Image";
import { HotelRoomTitle } from "@/features/hotels/components/hotel/HotelRoomTitle";
import { RoomDetailsDrawer } from "@/features/hotels/components/RoomSelection/RoomDetailsDrawer";
import type {
  HotelBooking,
  HotelBookingRoomOption,
  HotelData,
} from "@/features/hotels/types/types";
import { getNumberOfBookingNights } from "@/features/hotels/utils/getNumberOfBookingNights";
import { copyToClipboardWithFeedback } from "@/utils/copyToClipboard";

const DetailsRow = ({
  icon: Icon,
  label,
  secondaryLabel,
  copyMessage,
}: {
  icon: React.ElementType;
  label: React.ReactNode;
  secondaryLabel: React.ReactNode;
  copyMessage?: string;
}) => {
  const secondaryLabelRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  return (
    <div className="flex gap-2 text-text-primary">
      <Icon className="size-5 shrink-0" />

      <div className="grow-1">
        <div className="mb-1 font-medium">{label}</div>
        <div className="flex justify-between text-sm text-text-tertiary">
          <div ref={secondaryLabelRef}>{secondaryLabel}</div>

          {copyMessage ? (
            <div className="shrink-0 -translate-y-1.5">
              <IconButton
                size="small"
                aria-label={intl.formatMessage({
                  defaultMessage: "Copy",
                  description: "ARIA label for copy button",
                })}
                onClick={() => {
                  const text = secondaryLabelRef.current?.textContent?.trim();
                  if (text) copyToClipboardWithFeedback(text, copyMessage);
                }}
              >
                <CopyIcon size={16} />
              </IconButton>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

type Props = {
  hotel: HotelData;
  hotelBookingData: HotelBooking;
  rooms?: { adults: number; children: number }[];
  checkIn: Date;
  checkOut: Date;
};

export const HotelAndRoomCard = ({
  hotel,
  hotelBookingData,
  rooms,
  checkIn,
  checkOut,
}: Props) => {
  const intl = useIntl();

  const bookingNights = getNumberOfBookingNights({
    checkIn,
    checkOut,
  });

  const [isRoomDetailsOpen, setIsRoomDetailsOpen] = useState(false);
  const [selectedRoomsDetails, setSelectedRoomsDetails] = useState<
    HotelBookingRoomOption[] | null
  >(null);

  return (
    <>
      <div className="section-card flex flex-col gap-4 pb-2">
        <div className="flex justify-between gap-4">
          <div className="text-lg font-semibold text-text-title">
            {hotel.name}
          </div>

          <Image
            className="h-17.5 w-15 shrink-0 rounded-lg object-cover"
            src={hotel.mainImageUrl || "/imagePlaceholder.svg"}
            alt={hotel.name}
          />
        </div>

        <Divider color="neutral-soft" />

        <DetailsRow
          icon={CalendarDotsIcon}
          label={
            <>
              {intl.formatDate(checkIn, {
                month: "short",
                day: "numeric",
                weekday: "short",
              })}{" "}
              -{" "}
              {intl.formatDate(checkOut, {
                month: "short",
                day: "numeric",
                weekday: "short",
                year: "numeric",
              })}
            </>
          }
          secondaryLabel={intl.formatMessage(
            {
              defaultMessage: "{count, plural, one {# night} other {# nights}}",
              description:
                "Night count used in booking summaries (e.g. '3 nights')",
            },
            { count: bookingNights },
          )}
        />

        <DetailsRow
          icon={MapPinIcon}
          label={intl.formatMessage({
            defaultMessage: "Location",
            description: "Label for the location of the hotel",
          })}
          secondaryLabel={hotel.address}
          copyMessage={
            hotel.address
              ? intl.formatMessage({
                  defaultMessage: "Hotel address copied",
                  description: "Snackbar shown after copying the hotel address",
                })
              : undefined
          }
        />

        <DetailsRow
          icon={BedIcon}
          label={intl.formatMessage({
            defaultMessage: "Selected room",
            description: "Label for the selected room in the hotel",
          })}
          secondaryLabel={
            <ul>
              {rooms &&
                hotelBookingData.rooms.map((room, index) => (
                  <li key={room.roomToken}>
                    <HotelRoomTitle
                      name={room.name}
                      board={room.boardBasis}
                      occupancy={{
                        numberOfAdults: rooms[index].adults,
                        numberOfChildren: rooms[index].children,
                      }}
                    />
                  </li>
                ))}
            </ul>
          }
        />

        <Button
          type="button"
          variant="text"
          size="small"
          fullWidth
          onClick={() => {
            setSelectedRoomsDetails(hotelBookingData.rooms);
            setIsRoomDetailsOpen(true);
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Room details",
            description:
              "Section heading for room details within a hotel booking",
          })}
        </Button>
      </div>

      <RoomDetailsDrawer
        open={isRoomDetailsOpen}
        onClose={() => setIsRoomDetailsOpen(false)}
        hotel={hotel}
        rooms={selectedRoomsDetails}
      />
    </>
  );
};
